/**
 * Switch: blue when on (unlocked or active), edge grey when off (locked or inactive).
 * Never red: red is reserved for live, locked-state marks and destructive actions.
 */
export interface SwitchProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
  compact?: boolean;
}

export function Switch({
  checked,
  onChange,
  label,
  disabled = false,
  compact = false,
}: SwitchProps) {
  const track = compact ? "h-5 w-9" : "h-6 w-11";
  const knob = compact ? "size-3.5" : "size-4";
  const shift = compact ? "translate-x-4" : "translate-x-5";
  // A pseudo-element grows the tap target to 44px without changing the drawn track.
  const hitArea = compact
    ? "before:-inset-x-1 before:-inset-y-3"
    : "before:-inset-x-0 before:-inset-y-2.5";
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      data-state={checked ? "on" : "off"}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={[
        "relative inline-flex shrink-0 items-center rounded-full p-0.5 transition-colors duration-[120ms]",
        "before:absolute before:content-['']",
        "disabled:cursor-not-allowed disabled:opacity-50",
        hitArea,
        track,
        checked ? "bg-blue" : "bg-edge",
      ].join(" ")}
    >
      <span
        aria-hidden="true"
        className={[
          "block rounded-full bg-white shadow-sm transition-transform duration-[120ms]",
          knob,
          checked ? shift : "translate-x-0",
        ].join(" ")}
      />
    </button>
  );
}
